import { Color3, Color4, Vector2, Vector3 } from '@babylonjs/core';
import { Asset, AssetType } from './Asset';
import { AssetFactory, ManifestEntry } from './AssetFactory';
import { AssetObject } from './AssetObject';
import manifest from './assets/manifest.json';
import { AbsolutePlacement, DecorationConfig } from './DecorationGenerator';
import { DungeonConfig } from './DungeonGenerator';
import { EnemyConfig } from './EnemyGenerator';
import { FogConfig } from './FogConfig';
import {
  BaseSceneConfig,
  CameraConfig,
  CollectibleDistributionConfig,
  GrassConfig,
  LevelConfig,
  MaterialConfig,
  PathConfig,
  PathLightsConfig,
  PlatformAssetConfig,
  PlatformGenerationConfig,
  PlayerConfig,
  SpriteConfig,
  WaterConfig,
  WayGenerationConfig,
} from './LevelConfig';
import { LightConfig } from './LightConfig';
import { GroundConfig, HeightMapConfig, SkyBoxConfig } from './MeshFactory';
import { ObstacleConfig } from './ObstacleGenerator';
import { TextureAtlas } from './TextureAtlas';
/**
 * Utility class to load LevelConfig from JSON files
 */
export class LevelConfigLoader {
  static getLevelIndex(key: string): number {
    const levels: ManifestEntry[] = [];
    AssetFactory.findManifestFiles('.json', manifest as ManifestEntry, 'levels/', levels);
    for (const level of levels) {
      if (level.name === key + '.json') {
        return levels.indexOf(level);
      }
    }
    return -1;
  }

  static isLastLevel(key: string): boolean {
    if (key === 'test') {
      return false;
    }
    const levels: ManifestEntry[] = [];
    AssetFactory.findManifestFiles('.json', manifest as ManifestEntry, 'levels/', levels);
    return levels[levels.length - 1].name === key + '.json';
  }

  static getLevelKey(index: number): string {
    const levels: ManifestEntry[] = [];
    AssetFactory.findManifestFiles('.json', manifest as ManifestEntry, 'levels/', levels);
    for (const level of levels) {
      if (levels.indexOf(level) === index) {
        const nextLevel = levels[index + 1];
        const key = nextLevel.name.replace('.json', '');
        return key;
      }
    }
    return 'test';
  }

  static async getLevelName(key: string): Promise<string> {
    const levels: ManifestEntry[] = [];
    AssetFactory.findManifestFiles('.json', manifest as ManifestEntry, 'levels/', levels);
    for (const level of levels) {
      if (level.name === key + '.json') {
        const config = await LevelConfigLoader.load(key);
        return config.name;
      }
    }
    return 'Test';
  }

  static async getLevelNames(): Promise<string[]> {
    const levels: ManifestEntry[] = [];
    const names = [];
    AssetFactory.findManifestFiles('.json', manifest as ManifestEntry, 'levels/', levels);
    for (const level of levels) {
      const config = await LevelConfigLoader.load(level.name.replace('.json', ''));
      names.push(config.name);
    }
    return names;
  }

  /**
   * Load a level configuration from a JSON file
   * @param key The level key (e.g., "test" loads "assets/levels/test.json")
   */
  static async load(key: string): Promise<LevelConfig> {
    const url = `assets/levels/${key}.json`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to load level config: ${url} (${response.status})`);
    }

    const json = await response.json();
    return this.parseLevelConfig(json);
  }

  /**
   * Parse a Vector3 from JSON format {_x, _y, _z} or  {x, y, z}
   */
  static parseVector3(obj: { _x?: number; _y?: number; _z?: number; x?: number; y?: number; z?: number } | undefined): Vector3 | undefined {
    if (!obj) return undefined;
    const x = obj._x ?? obj.x ?? 0;
    const y = obj._y ?? obj.y ?? 0;
    const z = obj._z ?? obj.z ?? 0;
    return new Vector3(x, y, z);
  }

  /**
   * Parse a Vector2 from JSON format {_x, _y} or {x, y}
   */
  static parseVector2(obj: { _x?: number; _y?: number; x?: number; y?: number } | undefined): Vector2 | undefined {
    if (!obj) return undefined;
    const x = obj._x ?? obj.x ?? 0;
    const y = obj._y ?? obj.y ?? 0;
    return new Vector2(x, y);
  }

  /**
   * Parse a Color3 from JSON format {r, g, b}
   */
  static parseColor3(obj: { r: number; g: number; b: number } | undefined): Color3 | undefined {
    if (!obj) return undefined;
    return new Color3(obj.r, obj.g, obj.b);
  }

  /**
   * Parse a Color4 from JSON format {r, g, b, a}
   */
  static parseColor4(obj: { r: number; g: number; b: number; a: number } | undefined): Color4 | undefined {
    if (!obj) return undefined;
    return new Color4(obj.r, obj.g, obj.b, obj.a);
  }

  /**
   * Parse LightConfig from JSON
   */
  static parseLightConfig(json: any): LightConfig {
    const config = new LightConfig();
    if (json.hemiDirection) config.hemiDirection = this.parseVector3(json.hemiDirection)!;
    if (json.hemiIntensity !== undefined) config.hemiIntensity = json.hemiIntensity;
    if (json.hemiGroundColor) config.hemiGroundColor = this.parseColor3(json.hemiGroundColor)!;
    if (json.direction) config.direction = this.parseVector3(json.direction)!;
    if (json.position) config.position = this.parseVector3(json.position)!;
    if (json.diffuse) config.diffuse = this.parseColor3(json.diffuse)!;
    if (json.lensflare !== undefined) config.lensflare = json.lensflare;
    if (json.intensity !== undefined) config.intensity = json.intensity;
    if (json.darkness !== undefined) config.darkness = json.darkness;
    if (json.mapSize !== undefined) config.mapSize = json.mapSize;
    if (json.usePercentageCloserFiltering !== undefined) config.usePercentageCloserFiltering = json.usePercentageCloserFiltering;
    if (json.useExponentialShadowMap !== undefined) config.useExponentialShadowMap = json.useExponentialShadowMap;
    if (json.useBlurExponentialShadowMap !== undefined) config.useBlurExponentialShadowMap = json.useBlurExponentialShadowMap;
    if (json.useKernelBlur !== undefined) config.useKernelBlur = json.useKernelBlur;
    if (json.usePoissonSampling !== undefined) config.usePoissonSampling = json.usePoissonSampling;
    if (json.shadowCasterDistance !== undefined) config.shadowCasterDistance = json.shadowCasterDistance;
    return config;
  }

  /**
   * Parse SkyBoxConfig from JSON
   */
  static parseSkyBoxConfig(json: any): SkyBoxConfig {
    if (!json) return undefined;

    json.clearColor = this.parseColor4(json.clearColor);
    json.position = this.parseVector3(json.position);
    json.rotation = this.parseVector3(json.rotation);

    return json as SkyBoxConfig;
  }

  /**
   * Parse MaterialConfig from JSON
   */
  static parseMaterialConfig(json: any): MaterialConfig {
    return {
      colorMaterialBaseColor: this.parseColor3(json.colorMaterialBaseColor),
      colorMaterialCount: json.colorMaterialCount,
      textures: json.textures || [],
      globalTextureNames: json.globalTextureNames || [],
      materials: json.materials || [],
    };
  }

  /**
   * Parse FogConfig from JSON
   */
  static parseFogConfig(json: any): FogConfig | undefined {
    if (!json) return undefined;
    const config = new FogConfig();
    Object.assign(config, json);
    config.color = this.parseColor3(json.color);
    return config;
  }

  /**
   * Parse GroundConfig from JSON
   */
  static parseGroundConfig(json: any): GroundConfig | undefined {
    if (!json) return undefined;
    const config = new GroundConfig();
    Object.assign(config, json);
    config.position = this.parseVector3(json.position) || Vector3.Zero();
    config.size = this.parseVector3(json.size) || Vector3.One();
    config.rotation = this.parseVector3(json.rotation);
    config.highlight = this.parseColor3(json.highlight);
    return config;
  }

  /**
   * Parse HeightMapConfig from JSON
   */
  static parseHeightMapConfig(json: any): HeightMapConfig | undefined {
    if (!json) return undefined;
    const config = new HeightMapConfig();
    Object.assign(config, json);
    config.position = this.parseVector3(json.position) || Vector3.Zero();
    config.size = this.parseVector3(json.size) || Vector3.One();
    config.rotation = this.parseVector3(json.rotation);
    config.highlight = this.parseColor3(json.highlight);
    config.terrainTextureScale = this.parseVector3(json.terrainTextureScale) || Vector3.One();
    config.specularColor = this.parseColor3(json.specularColor) || new Color3(0.5, 0.5, 0.5);
    config.diffuseColor = this.parseColor3(json.diffuseColor) || new Color3(0.5, 0.5, 0.5);
    return config;
  }

  /**
   * Parse WayGenerationConfig from JSON
   */
  static parseWayConfig(json: any): WayGenerationConfig | undefined {
    if (!json) return undefined;
    return json as WayGenerationConfig;
  }

  /**
   * Parse Asset from JSON
   */
  static parseAsset(json: any): Asset {
    if (!json) return undefined;
    return new Asset(
      json.name,
      json.type as AssetType,
      json.rootUrl,
      json.fileName,
      json.meshNames,
      json.meshName,
      json.resetPosition,
      json.unParent,
      json.singleMesh,
      json.meshIndex
    );
  }

  /**
   * Parse Texture Atlas from JSON
   */
  static parseTextureAtlas(json: any): TextureAtlas {
    return new TextureAtlas(json.name, json.rootURL, json.imgURL, json.jsonURL, json.normalMapImgURL);
  }
  /**
   * Parse AssetObject from JSON
   */
  static parseAssetObject(json: any): AssetObject {
    const ao = new AssetObject(json.asset, json.name, json.scale, json.centerOrigin, json.clone, json.instance);
    ao.deepClone = json.deepClone;
    ao.type = json.type;
    ao.computeCenter = json.computeCenter;
    ao.metallic = json.metallic;
    ao.roughness = json.roughness;
    ao.scale = json.scale;
    ao.diffuseColor = this.parseColor3(json.diffuseColor);
    return ao;
  }

  /**
   * Parse PlatformAssetConfig from JSON
   */
  static parsePlatformAssetConfig(json: any): PlatformAssetConfig | undefined {
    if (!json) return undefined;

    return {
      assets: (json.assets || []).map((a: any) => this.parseAsset(a)),
      assetObjects: (json.assetObjects || []).map((ao: any) => this.parseAssetObject(ao)),
      scale: json.scale,
    };
  }

  /**
   * Parse CollectibleGenerationConfig from JSON
   */
  static parseCollectibleConfig(json: any): CollectibleDistributionConfig | undefined {
    if (!json) return undefined;
    const config = { ...json };
    config.offset = this.parseVector3(json.offset);
    return config as CollectibleDistributionConfig;
  }

  /**
   * Parse PlatformGenerationConfig from JSON
   */
  static parsePlatformConfig(json: any): PlatformGenerationConfig | undefined {
    if (!json) return undefined;

    // Deep copy and convert Vector3 objects
    const config = { ...json };

    if (json.platformSize) {
      config.platformSize = {
        min: this.parseVector3(json.platformSize.min)!,
        max: this.parseVector3(json.platformSize.max)!,
      };
    }
    if (json.pathOffset) {
      config.pathOffset = this.parseVector3(json.pathOffset);
    }
    if (json.startGroundSize) {
      config.startGroundSize = this.parseVector3(json.startGroundSize);
    }
    if (json.destinationGroundSize) {
      config.destinationGroundSize = this.parseVector3(json.destinationGroundSize);
    }

    // Parse platformAssets with proper class instances
    if (json.platformAsset) {
      config.platformAsset = this.parsePlatformAssetConfig(json.platformAsset);
    }
    if (json.movingPlatformAsset) {
      config.movingPlatformAsset = this.parsePlatformAssetConfig(json.movingPlatformAsset);
    }
    if (json.crumblingPlatformAsset) {
      config.crumblingPlatformAsset = this.parsePlatformAssetConfig(json.crumblingPlatformAsset);
    }
    if (json.startGroundAsset) {
      config.startGroundAsset = this.parsePlatformAssetConfig(json.startGroundAsset);
    }
    if (json.destinationGroundAsset) {
      config.destinationGroundAsset = this.parsePlatformAssetConfig(json.destinationGroundAsset);
    }
    return config as PlatformGenerationConfig;
  }

  /**
   * Parse DecorationConfig from JSON
   */
  static parseDecorationConfig(json: any): DecorationConfig | undefined {
    if (!json) return undefined;
    const parsed = {
      assets: (json.assets || []).map((a: any) => this.parseAsset(a)),
      assetObjects: (json.assetObjects || []).map((ao: any) => this.parseAssetObject(ao)),
      distributions: this.parseDistributions(json.distributions),
      spawnRange: json.spawnRange,
      despawnRange: json.despawnRange,
    };
    return parsed as DecorationConfig;
  }

  /**
   * Parse distributions array from JSON
   */
  static parseDistributions(json: any[]): any[] {
    if (!json || !Array.isArray(json)) return [];
    for (const distribution of json) {
      if (distribution.offset) {
        distribution.offset = this.parseVector3(distribution.offset);
      }
      if (distribution.rotation) {
        distribution.rotation = this.parseVector3(distribution.rotation);
      }
      if (distribution.bakeRotation) {
        distribution.bakeRotation = this.parseVector3(distribution.bakeRotation);
      }

      if (distribution.absolutePlacements) {
        distribution.absolutePlacements = distribution.absolutePlacements.map((a: any) => this.parseAbsolutePlacement(a));
      }
    }
    return json;
  }

  static parseAbsolutePlacement(json: any): AbsolutePlacement {
    return {
      position: this.parseVector3(json.position),
      rotation: this.parseVector3(json.rotation),
      scale: this.parseVector3(json.scale),
    };
  }

  /**
   * Parse WaterConfig from JSON
   */
  static parseWaterConfig(json: any): WaterConfig | undefined {
    if (!json) return undefined;
    json.waterColor = this.parseColor3(json.waterColor);
    json.windDirection = this.parseVector2(json.windDirection);
    json.renderTargetSize = this.parseVector2(json.renderTargetSize);
    return json as WaterConfig;
  }

  /**
   * Parse EnemyConfig from JSON
   */
  static parseEnemyConfig(json: any): EnemyConfig | undefined {
    if (!json) return undefined;
    return {
      assets: (json.assets || []).map((a: any) => this.parseAsset(a)),
      assetObjects: (json.assetObjects || []).map((ao: any) => this.parseAssetObject(ao)),
      distributions: this.parseDistributions(json.distributions),
      spawnRange: json.spawnRange,
      despawnRange: json.despawnRange,
    };
  }

  /**
   * Parse ObstacleConfig from JSON
   */
  static parseObstacleConfig(json: any): ObstacleConfig | undefined {
    if (!json) return undefined;
    return {
      assets: (json.assets || []).map((a: any) => this.parseAsset(a)),
      assetObjects: (json.assetObjects || []).map((ao: any) => this.parseAssetObject(ao)),
      distributions: this.parseDistributions(json.distributions),
      spawnRange: json.spawnRange,
      despawnRange: json.despawnRange,
    };
  }

  /**
   * Parse DungeonConfig from JSON
   */
  static parseDungeonConfig(json: any): DungeonConfig | undefined {
    if (!json) return undefined;

    json.hallMinSize = this.parseVector3(json.hallMinSize);
    json.hallMaxSize = this.parseVector3(json.hallMaxSize);
    json.corridorMinSize = this.parseVector3(json.corridorMinSize);
    json.corridorMaxSize = this.parseVector3(json.corridorMaxSize);

    return json as DungeonConfig;
  }

  /**
   * Parse PathConfig from JSON
   */
  static parsePathConfig(json: any): PathConfig | undefined {
    if (!json) return undefined;
    json.start = this.parseVector3(json.start);
    json.destination = this.parseVector3(json.destination);

    if (json.predefined) {
      const predefined = [];
      for (const p of json.predefined) {
        predefined.push(this.parseVector3(p));
      }
      json.predefined = predefined;
    }

    return json as PathConfig;
  }

  /**
   * Parse CameraConfig from JSON
   */
  static parseCameraConfig(json: any): CameraConfig | undefined {
    if (!json) return undefined;
    return json as CameraConfig;
  }

  /**
   * Parse GrassConfig from JSON
   */
  static parseGrassConfig(json: any): GrassConfig | undefined {
    if (!json) return undefined;
    json.baseColor = this.parseColor3(json.baseColor);
    json.tipColor = this.parseColor3(json.tipColor);
    return json as GrassConfig;
  }

  /**
   * Parse PlayerConfig from JSON
   */
  static parsePlayerConfig(json: any): PlayerConfig | undefined {
    if (!json) return undefined;
    json.startOffset = this.parseVector3(json.startOffset);
    return json as PlayerConfig;
  }

  /**
   * Parse BaseSceneConfig from JSON
   */
  static parseBaseSceneConfig(json: any): BaseSceneConfig | undefined {
    if (!json) return undefined;
    json.object = this.parseAssetObject(json.object);
    json.offset = this.parseVector3(json.offset);
    return json as BaseSceneConfig;
  }

  /**
   * Parse assets array from JSON
   */
  static parseAssets(json: any[]): Asset[] {
    if (!json || !Array.isArray(json)) return [];
    return json.map((a) => this.parseAsset(a));
  }

  static parseSprites(json: any[]): SpriteConfig[] {
    if (!json || !Array.isArray(json)) return [];
    return json.map((s) => this.parseSprite(s));
  }

  static parseSprite(json: any): SpriteConfig | undefined {
    if (!json) return undefined;

    json.asset = this.parseAsset(json.asset);
    json.color = this.parseColor4(json.color);
    return json as SpriteConfig;
  }

  /**
   * Parse assets array from JSON
   */
  static parseAtlasses(json: any[]): TextureAtlas[] {
    if (!json || !Array.isArray(json)) return [];
    return json.map((a) => this.parseTextureAtlas(a));
  }

  /**
   * Parse PathLightsConfig from JSON
   */
  static parsePathLightsConfig(json: any): PathLightsConfig | undefined {
    if (!json) return undefined;

    const colors: Color3[] = [];
    for (const color of json.colors) {
      colors.push(this.parseColor3(color));
    }
    json.colors = colors;
    json.offset = this.parseVector3(json.offset);
    json.direction = this.parseVector3(json.direction);
    return json as PathLightsConfig;
  }

  /**
   * Parse a complete LevelConfig from JSON
   */
  static parseLevelConfig(json: any): LevelConfig {
    const config = new LevelConfig(
      json.name,
      json.seed,
      json.timeLimit,
      this.parseVector3(json.gravity)!,
      json.killingHeight,
      json.heightAboveGround,
      json.checkpointInterval,
      json.goalEndOffset,
      this.parsePlayerConfig(json.player),
      this.parseCameraConfig(json.camera),
      this.parsePathConfig(json.path),
      this.parseLightConfig(json.light),
      this.parseSkyBoxConfig(json.skybox),
      this.parseMaterialConfig(json.material),
      this.parseFogConfig(json.fog),
      this.parseGroundConfig(json.ground),
      this.parseHeightMapConfig(json.heightMap),
      this.parseHeightMapConfig(json.ceiling),
      this.parseWayConfig(json.way),
      this.parsePlatformConfig(json.platform),
      this.parseCollectibleConfig(json.collectible),
      this.parseBaseSceneConfig(json.baseScene),
      this.parseDecorationConfig(json.decoration),
      this.parseEnemyConfig(json.enemy),
      this.parseObstacleConfig(json.obstacle),
      this.parseDungeonConfig(json.dungeon),
      this.parseWaterConfig(json.water),
      json.sound,
      json.postProcess,
      this.parsePathLightsConfig(json.pathLights),
      this.parseGrassConfig(json.grass),
      this.parseAssets(json.assets),
      this.parseSprites(json.sprites),
      this.parseAtlasses(json.atlasses)
    );

    return config;
  }
}
